import { useState } from 'react'
import { AlertTriangle, X } from 'lucide-react'
import { Button } from './Button'
import { Panel } from './Panel'

/**
 * P10-2 (docs/P10_PHASE1.md §3 item 8): a self-owned modal used everywhere a
 * destructive action needs a "本当に消しますか" confirmation — `window.confirm`
 * is deliberately never used (task spec: "window.confirm 禁止・Playwright と
 * 相性のため" — Playwright cannot interact with the native dialog at all).
 *
 * Stays mounted and OPEN across a failed `onConfirm` (task spec item 8: a 409
 * conflict must show inline and keep the dialog open, not close it) — the
 * caller decides success by unmounting/closing this component itself after
 * `onConfirm` resolves without throwing; this component never closes itself
 * except via `onCancel`/the header's ✕.
 */
export function ConfirmDialog({
  title,
  message,
  confirmLabel = '削除',
  cancelLabel = 'キャンセル',
  danger = true,
  onConfirm,
  onCancel,
  busyMessage = '処理中のため実行できません。しばらく待ってから再度お試しください。',
}: {
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  danger?: boolean
  /** Throw to keep the dialog open with an inline error; an Error with `.status === 409` shows `busyMessage` instead of its own message. */
  onConfirm: () => Promise<void> | void
  onCancel: () => void
  /**
   * Task spec item 8's literal copy is for project delete
   * ("取り込み／書き出し中のため削除できません"); other callers (composition
   * delete) pass their own wording. Defaults to a generic conflict message.
   */
  busyMessage?: string
}) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleConfirm() {
    setSubmitting(true)
    setError(null)
    try {
      await onConfirm()
      // Success: the caller is expected to close/unmount this dialog itself
      // (e.g. after removing the deleted row from its list) — this component
      // never closes on success on its own, so it can't race that update.
    } catch (e) {
      const status = (e as { status?: number } | null)?.status
      setError(status === 409 ? busyMessage : String((e as Error)?.message ?? e))
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60"
      data-testid="confirm-dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel()
      }}
    >
      <Panel className="w-[360px] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-2.5 mb-2">
          {danger && <AlertTriangle size={18} className="text-accent-red shrink-0 mt-0.5" />}
          <div className="text-sm font-medium flex-1">{title}</div>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="text-text-faint hover:text-text disabled:opacity-40"
            data-testid="confirm-dialog-close"
            aria-label="閉じる"
          >
            <X size={16} />
          </button>
        </div>
        <div className="text-xs text-text-muted mb-4 whitespace-pre-wrap">{message}</div>
        {error && (
          <div
            className="text-[11px] text-accent-red mb-3 break-words rounded bg-accent-red/10 border border-accent-red/30 px-2.5 py-2"
            data-testid="confirm-dialog-error"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={submitting}
            data-testid="confirm-dialog-cancel"
          >
            {cancelLabel}
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            size="sm"
            onClick={handleConfirm}
            disabled={submitting}
            data-testid="confirm-dialog-confirm"
          >
            {submitting ? '処理中…' : confirmLabel}
          </Button>
        </div>
      </Panel>
    </div>
  )
}
